import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Microscope, WandSparkles } from 'lucide-react';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import ResearchHeader from './ResearchHeader';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import DashboardCard from '@/components/ui/DashboardCard';
import SidebarLayout from '@/components/layout/SidebarLayout';
import MobilePageWrapper from '@/components/ui/MobilePageWrapper';
import { ResearchProvider, useResearchContext } from '@/contexts/research/ResearchContext';
import ResearchLightboxUniversal from './ResearchLightboxUniversal';
import ResearchAssistant from './ResearchAssistant';
import ResearchGenerationModal from './ResearchGenerationModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { usePublicResearchMatrixLogic } from '@/hooks/research-generator/usePublicResearchMatrixLogic';
import { useResearchCardActions } from '@/hooks/research-generator/useResearchCardActions';
import { useDeleteResearch } from '@/hooks/research-generator/library/use-delete-research';
import { useToast } from '@/hooks/errors/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useResearchGeneration } from '@/hooks/research-generator/use-research-generation';
import ResearchLoadingStates from './ResearchLoadingStates';
import ResearchEmptyState from './ResearchEmptyState';
import ResearchCardGrid from './ResearchCardGrid';
import ResearchLoadMoreButton from './ResearchLoadMoreButton';

// Inner component that uses the context
const ResearchMatrixContent: React.FC = () => {
  const { isMobile } = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state, actions } = useResearchContext();
  const generateResearchMutation = useResearchGeneration();
  const [isGeneratingRelatedQuestion, setIsGeneratingRelatedQuestion] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [researchParams, setResearchParams] = useState<any>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  const {
    showOnlyUserResearch,
    showFavorites,
    selectedResearch,
    isLightboxOpen,
    allResearch,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    isLoadingDetails,
    selectedResearchDetails,
    handleFilterChange,
    handleResearchClick,
    handleCloseLightbox,
    handleToggleFavorite,
    fetchNextPage,
    hasPrevious,
    hasNext,
    handlePreviousResearch,
    handleNextResearch,
    convertToResearchItem,
  } = usePublicResearchMatrixLogic();

  const {
    navigateToGenerateResearch,
    handleGenerateResearch: _unusedHandleGenerateResearch,
    handleResearchGenerated,
  } = useResearchCardActions({
    onResearchClick: handleResearchClick,
    onToggleFavorite: handleToggleFavorite,
  });

  // Handle research generation for public research
  const handleGenerateResearch = useCallback(() => {
    console.log('[PublicResearch] Generate research triggered - showing modal');
    
    // Prepare research parameters for modal
    const params = {
      researchType: state.selectedType,
      researchPrompt: state.researchPrompt,
      timeframe: state.timeframe && state.timeframe !== 'none' ? state.timeframe : undefined,
      country: state.country && state.country !== 'none' ? state.country : undefined,
      sourceType: state.sourceType && state.sourceType !== 'none' ? state.sourceType : undefined,
    };
    
    // Show the generation modal
    setResearchParams(params);
    setShowGenerationModal(true);
  }, [state]);

  // Handle generation modal close
  const handleGenerationModalClose = () => {
    setShowGenerationModal(false);
    setResearchParams(null);
  };

  // Handle generation success
  const handleGenerationSuccess = (research: any) => {
    console.log('[PublicResearch] Research generation successful, opening lightbox:', research);
    
    // Reset the form
    actions.resetState();
    
    // Close the modal
    handleGenerationModalClose();
    
    // Handle the generated research (open in lightbox)
    if (research) {
      handleResearchGenerated(research);
    }
  };

  // Delete research functionality
  const deleteResearchMutation = useDeleteResearch();

  // Enhanced close handler that clears URL parameter
  const handleCloseLightboxWithUrlCleanup = useCallback(() => {
    console.log('[ResearchMatrixContainer] Closing lightbox and clearing URL parameter');
    
    // Clear the research parameter from URL
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('research')) {
      searchParams.delete('research');
      const newUrl = searchParams.toString() 
        ? `${location.pathname}?${searchParams.toString()}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    }
    
    // Close the lightbox
    handleCloseLightbox();
  }, [location.search, location.pathname, navigate, handleCloseLightbox]);

  // URL Parameter Handling - Auto-open lightbox for specific research
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const researchId = searchParams.get('research');
    
    if (researchId && !isLightboxOpen) {
      console.log('[ResearchMatrixContainer] URL research parameter detected:', researchId);
      
      if (allResearch.length > 0) {
        // Find research in current list
        const targetResearch = allResearch.find(research => research.id === researchId);
        
        if (targetResearch) {
          console.log('[ResearchMatrixContainer] Opening lightbox for research:', targetResearch.title);
          handleResearchClick(targetResearch.id);
        } else {
          console.log('[ResearchMatrixContainer] Research not found in current list, will try direct fetch');
          // Research not in current list, try direct click which will fetch it
          handleResearchClick(researchId);
        }
      } else if (!isLoading) {
        // Research list not loaded yet, but not currently loading, try direct click
        console.log('[ResearchMatrixContainer] Research list not loaded, trying direct fetch for:', researchId);
        handleResearchClick(researchId);
      }
    }
  }, [location.search, isLightboxOpen, allResearch, handleResearchClick, isLoading]);

  // Check ownership when research details are loaded
  useEffect(() => {
    const checkOwnership = async () => {
      if (!selectedResearch || !selectedResearchDetails) {
        setIsOwner(false);
        return;
      }
      
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user && selectedResearchDetails.user_id === session.user.id) {
        setIsOwner(true);
      } else {
        setIsOwner(false);
      }
    };
    
    checkOwnership();
  }, [selectedResearch, selectedResearchDetails]);

  // Handle delete research
  const handleDeleteResearch = () => {
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteResearch = async () => {
    if (!selectedResearch) return;
    
    try {
      await deleteResearchMutation.mutateAsync(selectedResearch.id);
      setShowDeleteConfirmation(false);
      handleCloseLightboxWithUrlCleanup();
      toast({
        title: "Success",
        description: "Research deleted successfully",
      });
    } catch (error) {
      console.error('Failed to delete research:', error);
      toast({
        title: "Error",
        description: "Failed to delete research",
        variant: "destructive",
      });
    }
  };

  const cancelDeleteResearch = () => {
    setShowDeleteConfirmation(false);
  };

  // Handle generation state changes with 60-second timeout
  const handleGenerationStateChange = (isGenerating: boolean) => {
    console.log('[ResearchMatrixContainer] Generation state changed:', isGenerating);
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setIsGeneratingRelatedQuestion(isGenerating);

    if (isGenerating) {
      // Set 60-second timeout as safety mechanism
      timeoutRef.current = setTimeout(() => {
        console.log('[ResearchMatrixContainer] 60-second timeout reached, resetting generation state');
        setIsGeneratingRelatedQuestion(false);
        timeoutRef.current = null;
      }, 60000);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Prepare stable component references to prevent unnecessary re-renders
  const dashboardHeader = useMemo(() => (
    <DashboardHeader 
      title="Public Research Generator"
      description="Generate research, save findings, explore insights"
      icon={<Microscope size={24} />}
      primaryAction={{
        label: "New Research",
        icon: WandSparkles,
        onClick: navigateToGenerateResearch,
        variant: "primary"
      }}
    />
  ), [navigateToGenerateResearch]);
  
  // Memoize the sidebar content to prevent re-initialization during layout changes
  const sidebarContent = useMemo(() => (
    <ResearchAssistant 
      onGenerateResearch={handleGenerateResearch} 
      onResearchGenerated={handleResearchGenerated}
      generateResearchMutation={generateResearchMutation}
    />
  ), [handleGenerateResearch, handleResearchGenerated, generateResearchMutation]);

  return (
    <MobilePageWrapper>
      <Container>
        {dashboardHeader}
        
        <div className="space-y-3 md:space-y-6">
          <SidebarLayout sidebar={sidebarContent}>
            <DashboardCard variant="purple" className="p-3 md:p-6">
              {/* Research Header with tabs */}
              <ResearchHeader
                showOnlyUserResearch={showOnlyUserResearch}
                showFavorites={showFavorites}
                onFilterChange={handleFilterChange}
                isLoading={isLoading}
              />
              
              <div className="space-y-2">
                {/* Loading States */}
                <ResearchLoadingStates
                  isLoading={isLoading}
                  error={error}
                  hasData={allResearch.length > 0}
                />

                {/* Empty State */}
                <ResearchEmptyState
                  isLoading={isLoading}
                  hasError={!!error}
                  hasData={allResearch.length > 0}
                />

                {/* Research Grid */}
                <ResearchCardGrid
                  research={allResearch}
                  isLoading={isLoading}
                  onResearchClick={handleResearchClick}
                  onToggleFavorite={handleToggleFavorite}
                  hideStorageButton={true}
                />

                {/* Load More Button */}
                <ResearchLoadMoreButton
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  onLoadMore={fetchNextPage}
                />
              </div>
            </DashboardCard>
          </SidebarLayout>
        </div>

        {/* Research Lightbox - Only render when needed */}
        {isLightboxOpen && (
          <ResearchLightboxUniversal
            research={selectedResearch}
            allResearch={allResearch}
            isOpen={isLightboxOpen}
            onClose={handleCloseLightboxWithUrlCleanup}
            isLoading={isLoadingDetails}
            researchDetails={selectedResearchDetails}
            onRelatedQuestionClick={handleResearchClick}
            // Navigation props - now with actual functionality
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            isLoadingMore={isFetchingNextPage}
            hasNextPage={hasNextPage}
            onPrevious={handlePreviousResearch}
            onNext={handleNextResearch}
            onLoadMore={fetchNextPage}
            // Generation state
            isGeneratingRelatedQuestion={isGeneratingRelatedQuestion}
            onGenerationStateChange={handleGenerationStateChange}
            // Delete functionality
            isOwner={isOwner}
            isDeleteLoading={deleteResearchMutation.isPending}
            onDelete={handleDeleteResearch}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirmation}
          onConfirm={confirmDeleteResearch}
          onCancel={cancelDeleteResearch}
          title="Delete Research"
          description={`Are you sure you want to delete "${selectedResearch?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          isConfirmLoading={deleteResearchMutation.isPending}
          confirmVariant="red"
        />
        
        {/* Research Generation Modal - Beautiful Progress */}
        <ResearchGenerationModal
          isOpen={showGenerationModal}
          onClose={handleGenerationModalClose}
          researchParams={researchParams}
          researchMutation={generateResearchMutation}
          onSuccess={handleGenerationSuccess}
        />
      </Container>
    </MobilePageWrapper>
  );
};

// Main container component that provides the context
export const ResearchMatrixContainer: React.FC = () => {
  return (
    <ResearchProvider>
      <ResearchMatrixContent />
    </ResearchProvider>
  );
};

export default ResearchMatrixContainer;
